import { useState, useCallback, useMemo } from "react";
import type { QuizFilter, Question } from "@shikaku/shared";
import {
  useQuestionTracker,
  useStudyRecord,
  useQuizEngine,
  useQuestions,
  BottomNav,
  Icons,
  HomeView,
  OfficialView,
  ReviewView,
  HistoryView,
  QuizView,
  ResultView,
  MemorizeView,
} from "@shikaku/ui";
import type { TabId } from "@shikaku/ui";
import { config, NAMESPACE } from "./config";
import { studyPathways } from "./pathways";
import { officialResources } from "./resources";

type Screen =
  | { type: "home" }
  | { type: "quiz" }
  | { type: "memorize"; questions: Question[] }
  | { type: "result"; score: number; total: number };

function filterQuestions(
  allQuestions: Question[],
  filter: QuizFilter,
  tracker: ReturnType<typeof useQuestionTracker>,
  unit?: string,
  subunit?: string,
  difficulty?: number,
): Question[] {
  let qs = [...allQuestions];
  if (unit) qs = qs.filter((q) => q.unit === unit);
  if (subunit) qs = qs.filter((q) => q.subunit === subunit);
  if (difficulty) qs = qs.filter((q) => q.difficulty === difficulty);

  switch (filter) {
    case "bookmarked":
      qs = qs.filter((q) => tracker.bookmarkedIDs.has(q.id));
      break;
    case "wrongOnly":
      qs = qs.filter((q) => tracker.wrongIDs.has(q.id));
      break;
    case "forgettingCurve": {
      const due = tracker.dueForReviewIDs;
      qs = qs.filter((q) => due.has(q.id));
      break;
    }
    case "all":
      break;
  }
  return qs;
}

export default function App() {
  const tracker = useQuestionTracker(NAMESPACE);
  const record = useStudyRecord(NAMESPACE);
  const engine = useQuizEngine();
  const { questions, loading } = useQuestions(
    config.questionFileJa,
    config.shufflesChoiceOrder,
  );

  const [screen, setScreen] = useState<Screen>({ type: "home" });
  const [activeTab, setActiveTab] = useState<TabId>("home");

  const startQuiz = useCallback(
    (filter: QuizFilter, unit?: string, subunit?: string, difficulty?: number) => {
      engine.loadQuestions(questions, filter, unit, subunit, difficulty, tracker);
      record.saveLastLesson("quiz", unit, subunit, difficulty);
      setScreen({ type: "quiz" });
    },
    [questions, engine, tracker, record],
  );

  const startMemorize = useCallback(
    (filter: QuizFilter, unit?: string, subunit?: string, difficulty?: number) => {
      const qs = filterQuestions(questions, filter, tracker, unit, subunit, difficulty);
      const shuffled = [...qs].sort(() => Math.random() - 0.5);
      record.saveLastLesson("memorize", unit, subunit, difficulty);
      setScreen({ type: "memorize", questions: shuffled });
    },
    [questions, tracker, record],
  );

  const goHome = useCallback(() => setScreen({ type: "home" }), []);

  const tabs = useMemo(
    () => [
      { id: "home" as TabId, label: "ホーム", icon: <Icons.Home /> },
      { id: "official" as TabId, label: "公式", icon: <Icons.ExternalLink /> },
      { id: "review" as TabId, label: "復習", icon: <Icons.Refresh /> },
      { id: "history" as TabId, label: "記録", icon: <Icons.Clock /> },
    ],
    [],
  );

  // ─── Loading ───────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: 12, background: "#f5f5f5" }}>
        <div style={{ width: 36, height: 36, border: "3px solid #e0e0e0", borderTopColor: config.primaryColor, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <div style={{ fontSize: 15, fontWeight: 600, color: config.primaryColor }}>
          {config.nameJa}
        </div>
        <div style={{ fontSize: 13, color: "#999" }}>読み込み中...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ─── Full-screen modes ─────────────────────────
  if (screen.type === "quiz") {
    return (
      <QuizView
        engine={engine}
        record={record}
        tracker={tracker}
        accentColor={config.primaryColor}
        onFinish={() =>
          setScreen({ type: "result", score: engine.score, total: engine.questions.length })
        }
        onBack={goHome}
      />
    );
  }

  if (screen.type === "result") {
    return (
      <ResultView
        score={screen.score}
        total={screen.total}
        accentColor={config.primaryColor}
        onRetry={() => { engine.reset(); setScreen({ type: "quiz" }); }}
        onRetryWrong={
          engine.sessionWrongQuestions.length > 0
            ? () => { engine.loadPresetQuestions(engine.sessionWrongQuestions); setScreen({ type: "quiz" }); }
            : undefined
        }
        onHome={goHome}
      />
    );
  }

  if (screen.type === "memorize") {
    return (
      <MemorizeView
        questions={screen.questions}
        tracker={tracker}
        accentColor={config.primaryColor}
        onBack={goHome}
      />
    );
  }

  // ─── Tab-based home ────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#f5f5f5" }}>
      {/* Clean header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e8e8e8", padding: "12px 20px", flexShrink: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#333" }}>
          {config.nameJa}
        </div>
        <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
          {config.totalQuestionCount}問収録
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {activeTab === "home" && (
          <HomeView
            questions={questions}
            record={record}
            tracker={tracker}
            primaryColor={config.primaryColor}
            usesUnitModeSelection={config.usesUnitModeSelection}
            studyPathways={studyPathways}
            onStartQuiz={startQuiz}
            onStartMemorize={startMemorize}
          />
        )}
        {activeTab === "official" && (
          <OfficialView resources={officialResources} primaryColor={config.primaryColor} />
        )}
        {activeTab === "review" && (
          <ReviewView
            questions={questions}
            tracker={tracker}
            primaryColor={config.primaryColor}
            onStartQuiz={(filter, subunit) => startQuiz(filter, undefined, subunit)}
          />
        )}
        {activeTab === "history" && (
          <HistoryView
            questions={questions}
            record={record}
            tracker={tracker}
            primaryColor={config.primaryColor}
          />
        )}
      </div>

      <BottomNav
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        primaryColor={config.primaryColor}
      />
    </div>
  );
}
